import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, AlertTriangle, CheckCircle2, ArrowLeft, Info } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal, Overline } from "../components/Reveal";
import { CLUB_PRESENTATIONS } from "../data/content";

export default function ClubPresentation() {
  const { slug } = useParams();
  const data = CLUB_PRESENTATIONS[slug];
  if (!data) return <Navigate to={`/travel-club-reviews/${slug}`} replace />;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `What to Expect at an ${data.name} Presentation`,
    description: data.intro,
    image: data.hero.image,
    author: { "@type": "Organization", name: "Travel Club Navigator Group" },
    publisher: { "@type": "Organization", name: "Travel Club Navigator Group" },
    keywords: data.keywords?.join(", "),
  };

  return (
    <>
      <SEO
        title={`What to Expect at an ${data.name} Presentation`}
        description={data.intro}
        path={`/travel-club-reviews/${slug}/presentation`}
        image={data.hero.image}
        keywords={data.keywords}
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="relative flex min-h-[62vh] items-end overflow-hidden">
        <img src={data.hero.image} alt={data.hero.alt} className="absolute inset-0 h-full w-full object-cover" />
        <div className="hero-scrim absolute inset-0" />
        <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-32 lg:px-8">
          <Link
            to={`/travel-club-reviews/${slug}`}
            data-testid="club-presentation-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-200 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" /> Back to {data.name} review
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mt-5 max-w-2xl"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/90 px-4 py-1.5 text-sm font-semibold text-white">
              <ShieldCheck className="h-4 w-4" /> Consumer Advocate Report
            </span>
            <h1 className="mt-5 font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
              What to Expect at an {data.name} Presentation
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-200">{data.intro}</p>
          </motion.div>
        </div>
      </section>

      {/* LOGO + DISCLAIMER */}
      <section className="mx-auto max-w-7xl px-5 pt-16 lg:px-8">
        <Reveal>
          <div className="flex flex-col items-start gap-4 rounded-3xl border border-slate-100 bg-white p-6 shadow-sm sm:flex-row sm:items-center">
            <span className="flex h-16 w-40 flex-none items-center justify-center rounded-xl bg-white">
              <img
                src={data.logo}
                alt={`${data.name} logo`}
                className="h-full w-full object-contain"
              />
            </span>
            <div className="flex items-start gap-2 text-sm text-slate-500">
              <Info className="mt-0.5 h-4 w-4 flex-none text-secondary" />
              <p>
                This report aggregates publicly available consumer reviews and complaints
                (e.g., BBB, Trustpilot, Yelp). Experiences vary; always verify current terms
                directly with {data.name} before making any decision.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* WHAT TO EXPECT */}
      <section className="section-pad mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Overline>What To Expect</Overline>
          <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
            The presentation, step by step.
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {data.expectations.map((item, i) => (
            <Reveal key={item.title} delay={i * 0.07}>
              <article className="flex h-full gap-5 rounded-3xl card-3d p-8">
                <span className="flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-secondary/10 font-display text-lg font-bold text-secondary">
                  {i + 1}
                </span>
                <div>
                  <h3 className="font-display text-xl font-bold text-navy">{item.title}</h3>
                  <p className="mt-2 leading-relaxed text-slate-600">{item.body}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TACTICS */}
      <section className="bg-muted section-pad">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <Overline>Common Tactics</Overline>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
              The pressure plays — decoded.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {data.tactics.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.07}>
                <article className="flex h-full gap-5 rounded-3xl border border-primary/15 bg-white p-8 shadow-sm">
                  <AlertTriangle className="h-7 w-7 flex-none text-primary" />
                  <div>
                    <h3 className="font-display text-xl font-bold text-navy">{t.title}</h3>
                    <p className="mt-2 leading-relaxed text-slate-600">{t.body}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROTECT */}
      <section className="section-pad mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <Overline>Protect Your Assets</Overline>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
              Walk in prepared. Walk out in control.
            </h2>
            <p className="mt-4 leading-relaxed text-slate-600">
              You can still attend for the incentive — just go in with a plan. Keep this
              checklist on your phone before your {data.name} session.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <ul className="space-y-4 rounded-3xl card-3d p-8">
              {data.protect.map((tip) => (
                <li key={tip} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-secondary" />
                  <span className="text-slate-700">{tip}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>
    </>
  );
}
